import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Upload, X, FileText, ShieldAlert, ArrowRight, User, GraduationCap, Compass, Plane, MessageSquare, Download } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import MapleLeaf from "@/components/MapleLeaf";

const COUNTRIES = [
  "Bangladesh", "India", "Indonesia", "Malaysia", "Pakistan", "Sri Lanka",
  "Nepal", "Bhutan", "Vietnam", "Thailand", "Philippines", "Singapore",
  "United Arab Emirates", "Saudi Arabia", "Qatar", "Oman", "Kuwait", "Bahrain",
  "Egypt", "Nigeria", "Ghana", "Kenya", "South Africa", "Ethiopia",
  "United Kingdom", "United States", "Canada", "Australia", "Germany", "France",
  "China", "Japan", "South Korea", "Turkey", "Brazil", "Argentina",
  "Other",
];

const INITIAL = {
  agent_name: "",
  full_name: "", age: "", gender: "", country_of_residence: "", nationality: "",
  email: "", whatsapp: "",
  currently_studying: "", institution: "", current_program: "", level_of_study: "",
  preferred_destination: "", preferred_duration: "", area_of_interest: "",
  travelled_internationally: "", countries_visited: "",
  message: "",
  declaration_accepted: false, privacy_consent: false,
};

const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];

function Field({ label, name, value, onChange, type = "text", required, testId, placeholder, maxLength }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</span>
      <input
        type={type} name={name} value={value} onChange={onChange} required={required}
        placeholder={placeholder} maxLength={maxLength}
        data-testid={testId}
        className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-[#D52B1E] focus:ring-2 focus:ring-red-100 text-sm outline-none transition-colors"
      />
    </label>
  );
}

function Select({ label, name, value, onChange, options, required, testId }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</span>
      <select
        name={name} value={value} onChange={onChange} required={required}
        data-testid={testId}
        className="mt-1.5 w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-[#D52B1E] focus:ring-2 focus:ring-red-100 text-sm outline-none transition-colors"
      >
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function SectionHeader({ Icon, step, title, subtitle }) {
  return (
    <div className="flex items-start gap-4 mb-5">
      <div className="w-11 h-11 rounded-2xl bg-[#FFE9E6] text-[#D52B1E] grid place-items-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-[11px] font-bold text-[#D52B1E] uppercase tracking-widest">Step {step}</p>
        <h2 className="font-display text-xl font-bold text-slate-900 leading-tight">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

export default function Apply() {
  const [sp] = useSearchParams();
  const [form, setForm] = useState(() => ({
    ...INITIAL,
    preferred_destination: sp.get("destination") || "",
  }));
  const [files, setFiles] = useState([]);      // [{ id, filename, size }]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onFileChange = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    if (files.length + chosen.length > MAX_FILES) {
      toast.error(`You can attach at most ${MAX_FILES} files.`);
      return;
    }
    setUploading(true);
    try {
      for (const file of chosen) {
        if (!ALLOWED_MIME.includes(file.type)) {
          toast.error(`${file.name}: only PDF, JPG, JPEG, PNG allowed.`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          toast.error(`${file.name}: exceeds 10 MB.`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, data]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.declaration_accepted || !form.privacy_consent) {
      toast.error("Please confirm the consent checkbox to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        document_ids: files.map((f) => f.id),
      };
      await api.post("/applications", payload);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="apply-success">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 grid place-items-center mb-6">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 mb-3">Thank You!</h1>
        <p className="text-slate-600 mb-8">
          We have received your inquiry. A StudyTrip Global representative or authorized local partner will contact you regarding suitable programs and next steps.
        </p>
        <Link to="/" className="inline-flex px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm shadow-lg shadow-orange-500/25 transition-colors">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-14" data-testid="apply-page">
      <div className="mb-10 text-center max-w-3xl mx-auto">
        <p className="inline-flex items-center gap-2 text-sm font-bold text-orange-600 uppercase tracking-widest mb-3">
          <MapleLeaf className="w-4 h-4" /> Apply Now
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">
          Start Your Global <span className="gradient-maple-text">Learning Journey</span>
        </h1>
        <p className="mt-4 text-slate-600">
          Interested in joining a StudyTrip Global international learning experience? Complete this short form and our team or authorized local partner will contact you with available programs and next steps.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <a
            href={`${API_BASE}/apply-form.pdf`}
            download="studytrip-global-apply-form.pdf"
            data-testid="apply-download-pdf"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 hover:border-orange-500 hover:text-orange-600 text-slate-800 font-semibold text-sm transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" /> Download printable form (PDF)
          </a>
          <span className="text-xs text-slate-400">Prefer offline? Print, complete and email it back.</span>
        </div>
      </div>

      <form onSubmit={submit} className="rounded-3xl bg-white border border-slate-100 shadow-lg shadow-red-500/5 p-6 md:p-10 space-y-10" data-testid="apply-form">
        {/* Agent */}
        <section>
          <SectionHeader Icon={User} step="1" title="Agent Information" subtitle="Optional — leave blank if you're applying directly." />
          <Field label="Agent name" name="agent_name" value={form.agent_name} onChange={onChange} testId="apply-agent-name" />
        </section>

        {/* Basic */}
        <section>
          <SectionHeader Icon={User} step="2" title="Basic Information" />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name" name="full_name" value={form.full_name} onChange={onChange} required testId="apply-fullname" />
            <Field label="Age" name="age" type="number" value={form.age} onChange={onChange} required testId="apply-age" />
            <Select label="Gender" name="gender" value={form.gender} onChange={onChange} options={["Male", "Female", "Prefer not to say"]} testId="apply-gender" />
            <Select label="Country of residence" name="country_of_residence" value={form.country_of_residence} onChange={onChange} options={COUNTRIES} required testId="apply-residence" />
            <Select label="Nationality" name="nationality" value={form.nationality} onChange={onChange} options={COUNTRIES} required testId="apply-nationality" />
            <Field label="Email address" name="email" type="email" value={form.email} onChange={onChange} required testId="apply-email" />
            <Field label="Mobile / WhatsApp number (include country code)" name="whatsapp" value={form.whatsapp} onChange={onChange} required testId="apply-whatsapp" placeholder="+1 825 365 8805" />
          </div>
        </section>

        {/* Education */}
        <section>
          <SectionHeader Icon={GraduationCap} step="3" title="Education Information" />
          <div className="grid md:grid-cols-2 gap-4">
            <Select label="Are you currently studying?" name="currently_studying" value={form.currently_studying} onChange={onChange} options={["Yes", "No", "Recently Graduated"]} required testId="apply-currently-studying" />
            <Field label="School / College / University name" name="institution" value={form.institution} onChange={onChange} testId="apply-institution" />
            <Field label="Current program / course" name="current_program" value={form.current_program} onChange={onChange} testId="apply-current-program" />
            <Select label="Current level of study" name="level_of_study" value={form.level_of_study} onChange={onChange} options={["Secondary / High School", "Certificate", "Diploma", "Undergraduate", "Postgraduate", "Other"]} testId="apply-level-of-study" />
          </div>
        </section>

        {/* Program interest */}
        <section>
          <SectionHeader Icon={Compass} step="4" title="Program Interest" />
          <div className="grid md:grid-cols-2 gap-4">
            <Select label="Preferred destination" name="preferred_destination" value={form.preferred_destination} onChange={onChange} options={["Canada", "Malaysia", "Indonesia", "Other / Not Sure Yet"]} required testId="apply-pref-dest" />
            <Select label="Preferred program duration" name="preferred_duration" value={form.preferred_duration} onChange={onChange} options={["5 Days", "7 Days", "10 Days", "14 Days", "Custom / Not Sure"]} testId="apply-duration" />
            <Select label="Area of interest" name="area_of_interest" value={form.area_of_interest} onChange={onChange} options={["Global Leadership", "Business & Entrepreneurship", "AI & Digital Innovation", "Technology", "Professional Development", "Hospitality & Tourism", "Cultural & Educational Experience", "International Trade", "Other"]} testId="apply-area-of-interest" />
          </div>
        </section>

        {/* Travel history */}
        <section>
          <SectionHeader Icon={Plane} step="5" title="Travel History" />
          <div className="grid md:grid-cols-2 gap-4">
            <Select label="Have you travelled internationally before?" name="travelled_internationally" value={form.travelled_internationally} onChange={onChange} options={["Yes", "No"]} required testId="apply-travelled" />
            {form.travelled_internationally === "Yes" && (
              <Field label="Countries previously visited" name="countries_visited" value={form.countries_visited} onChange={onChange} testId="apply-countries-visited" />
            )}
          </div>
        </section>

        {/* Documents */}
        <section>
          <SectionHeader Icon={Upload} step="6" title="Supporting Documents — Optional" subtitle="Not required for the initial inquiry. Documents are stored privately." />
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5" data-testid="apply-upload-zone">
            <p className="text-sm text-slate-600 mb-3">
              You may attach optional supporting documents (Student ID, enrolment letter, academic transcript etc.). Do NOT upload passport copies, banking or credit card information.
            </p>
            <p className="text-xs text-slate-500 mb-4">
              Allowed: PDF, JPG, JPEG, PNG · max 10 MB per file · up to {MAX_FILES} files.
            </p>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-[#D52B1E] cursor-pointer text-sm font-semibold text-slate-800 transition-colors">
              <Upload className="w-4 h-4" />
              {uploading ? "Uploading…" : "Choose files"}
              <input
                type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                multiple onChange={onFileChange} disabled={uploading || files.length >= MAX_FILES}
                data-testid="apply-file-input" className="hidden"
              />
            </label>
            {files.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="apply-file-list">
                {files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2">
                    <span className="inline-flex items-center gap-2 text-sm text-slate-700 min-w-0">
                      <FileText className="w-4 h-4 text-[#D52B1E] shrink-0" />
                      <span className="truncate">{f.filename}</span>
                      <span className="text-[11px] text-slate-400 shrink-0">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                    </span>
                    <button type="button" onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-600" aria-label="Remove">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Message */}
        <section>
          <SectionHeader Icon={MessageSquare} step="7" title="Anything you would like us to know?" subtitle="Optional · up to 500 characters." />
          <textarea
            name="message" value={form.message} onChange={onChange} rows={4} maxLength={500}
            data-testid="apply-message"
            className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-[#D52B1E] focus:ring-2 focus:ring-red-100 text-sm outline-none resize-none transition-colors"
          />
          <p className="mt-1 text-xs text-slate-400 text-right">{form.message.length} / 500</p>
        </section>

        {/* Consent + disclaimer */}
        <section className="rounded-2xl bg-[#FAF6EE] border border-amber-100 p-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" name="declaration_accepted" checked={form.declaration_accepted} onChange={onChange} data-testid="apply-declaration" className="mt-1 w-4 h-4 accent-[#D52B1E]" />
            <span className="text-sm text-slate-800 leading-relaxed">
              I confirm that the information provided is accurate and I consent to StudyTrip Global and its authorized partners contacting me regarding programs I have expressed interest in.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" name="privacy_consent" checked={form.privacy_consent} onChange={onChange} data-testid="apply-privacy-consent" className="mt-1 w-4 h-4 accent-[#D52B1E]" />
            <span className="text-sm text-slate-800 leading-relaxed">
              I have read and agree to the{" "}
              <Link to="/disclaimer#privacy" className="font-semibold text-[#D52B1E] hover:underline">Privacy Policy</Link>
              {" "}and{" "}
              <Link to="/disclaimer#terms" className="font-semibold text-[#D52B1E] hover:underline">Terms &amp; Conditions</Link>.
            </span>
          </label>

          <div className="rounded-xl bg-white/70 border border-amber-200 p-4">
            <p className="inline-flex items-center gap-2 text-xs font-bold text-amber-900 uppercase tracking-widest mb-2">
              <ShieldAlert className="w-3.5 h-3.5" /> Important Notice
            </p>
            <p className="text-xs text-amber-900 leading-relaxed">
              Submitting this form is an expression of interest only and does not guarantee admission, program availability, visa approval, travel authorization, entry into any country, academic credit, employment or any immigration outcome. Visa and entry decisions are made solely by the relevant government authorities. Program availability, schedules, fees and requirements are subject to confirmation. StudyTrip Global does not provide regulated immigration advice unless such services are separately provided through an appropriately authorized professional.
            </p>
          </div>
        </section>

        <button type="submit" disabled={submitting || uploading} data-testid="apply-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm uppercase tracking-widest disabled:opacity-60 shadow-xl shadow-orange-500/30 hover:-translate-y-0.5 transition-transform">
          {submitting ? "Submitting…" : <>Submit Application <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>

      <footer className="mt-12 text-center text-sm text-slate-500 space-y-1">
        <p className="font-display font-extrabold text-slate-900">StudyTrip Global</p>
        <p>An Initiative of Teach Me Online Inc., Canada</p>
        <p className="italic">Learn. Travel. Grow.</p>
        <a href="mailto:connect@studytripglobal.com" className="text-[#D52B1E] font-semibold hover:underline">connect@studytripglobal.com</a>
      </footer>
    </div>
  );
}
